import { useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { t } from "@/lib/i18n";

const ENDPOINTS = [
  { method: "GET / POST", path: "/loki/api/v1/query", desc: t("Logs zu einem Zeitpunkt") },
  { method: "GET / POST", path: "/loki/api/v1/query_range", desc: t("Logs über einen Zeitraum") },
  { method: "GET", path: "/loki/api/v1/labels", desc: t("Verfügbare Label-Namen") },
  { method: "GET", path: "/loki/api/v1/label/{name}/values", desc: t("Werte zu einem Label") },
  { method: "GET", path: "/loki/ready", desc: t("Health-Check") },
];

export function LokiPage() {
  const base = window.location.origin;
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  };

  const exampleCurl = `curl -X POST ${base}/loki/api/v1/query \\\n  -H "Content-Type: application/json" \\\n  -d '{"query": "{level=\\"ERROR\\"}", "limit": 10}'`;

  return (
    <div className="flex flex-col gap-4 p-6">
      <Card
        title={t("Loki HTTP API")}
        description={t("Read-Schnittstelle für Grafana. Leif ist Loki-kompatibel — keine separate Loki-Installation nötig.")}
        actions={
          <a
            href="/GRAFANA_SETUP.md"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:bg-muted"
          >
            <ExternalLink className="h-3 w-3" /> {t("Grafana-Setup")}
          </a>
        }
      >
        <div className="mb-3 flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2 font-mono text-xs">
          <span className="truncate">{base}/loki</span>
          <button
            type="button"
            onClick={() => copy(`${base}/loki`, "base")}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs hover:bg-muted"
          >
            <Copy className="h-3 w-3" /> {copied === "base" ? t("Kopiert") : t("Basis-URL")}
          </button>
        </div>

        <p className="text-xs text-muted-foreground">
          {t("In Grafana als")} <span className="font-mono">Loki</span>
          {t("-Datenquelle hinzufügen und die obige URL eintragen. Authentifizierung läuft über denselben Bearer-Token wie die Leif-API.")}
        </p>
      </Card>

      <Card title={t("Endpoints")}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="py-2 text-left font-medium">{t("Methode")}</th>
              <th className="py-2 text-left font-medium">{t("Pfad")}</th>
              <th className="py-2 text-left font-medium">{t("Beschreibung")}</th>
            </tr>
          </thead>
          <tbody>
            {ENDPOINTS.map((e) => (
              <tr key={e.path} className="border-b border-border last:border-b-0">
                <td className="py-2 pr-3 text-muted-foreground">{e.method}</td>
                <td className="py-2 pr-3 font-mono">{e.path}</td>
                <td className="py-2">{e.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title={t("Beispiel-Query")}>
        <pre className="overflow-x-auto rounded-md bg-muted/60 p-3 font-mono text-xs">{exampleCurl}</pre>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => copy(exampleCurl, "curl")}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:bg-muted"
          >
            <Copy className="h-3 w-3" /> {copied === "curl" ? t("Kopiert") : t("Kopieren")}
          </button>
        </div>
      </Card>
    </div>
  );
}
